import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import {
  useCameraAccessPopUp,
  useLanguagePopUp,
  useLoginPopUp,
  useLogoutPopUp,
  usePurchaseLoginPopUp,
  useVMSPopUp,
  useWelcomeBanner,
} from "@modules/ECMS/MAGP/Forms/Popup/Home.hook";
import { decodeHTML, encodeHTML } from "@utils/CommonUtils";
import { Button, Tabs } from "antd";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useHasAccess } from "~/Hooks/useHasAccess";
import { post, put } from "~/Services";
import { magpHost } from "~/Utils";
import { fields } from "./constants";
import { HyperLink } from "./HyperLink/HyperLink";
import { Icon } from "@iconify/react";

export const Home = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { openToast } = useNotification();
  const { hasRole } = useHasAccess();
  const activeTab = query.get("activeTab") || "cameraAccess";
  const { data: cameraData, error, isLoading, refetch: refetchCamera } = useCameraAccessPopUp();
  const {
    data: drilldownDataById,
    isLoading: isLoadingDoc,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");
  const { data: languageData, isLoading: languageLoading, refetch: refetchLanguage } = useLanguagePopUp();
  const {
    data: purchaseLoginData,
    isLoading: purchaseLoginLoading,
    refetch: refetchPurchaseLogin,
  } = usePurchaseLoginPopUp();
  const {
    data: welcomeBannerData,
    isLoading: welcomeBannerLoading,
    refetch: refetchWelcomeBanner,
  } = useWelcomeBanner();
  const { data: login, isLoading: loginLoading, refetch: refetchLogin } = useLoginPopUp();

  const { data: logoutData, isLoading: logoutLoading, refetch: refetchLogout } = useLogoutPopUp();
  const { data: VMSData, isLoading: VMSLoading, refetch: refetchVMS } = useVMSPopUp();

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
      return;
    }
    if (activeTab === "cameraAccess") {
      refetchCamera();
    } else if (activeTab === "languagePopUp") {
      refetchLanguage();
    } else if (activeTab === "purchaseLoginPopUp") {
      refetchPurchaseLogin();
    } else if (activeTab === "loginPopUp") {
      refetchLogin();
    } else if (activeTab === "logoutPopUp") {
      refetchLogout();
    } else if (activeTab === "VMSPopUp") {
      refetchVMS();
    } else if(activeTab === 'welcomeBanner'){
      refetchWelcomeBanner();
    }
  }, [activeTab]);

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
      return;
    }
    if (activeTab === "cameraAccess") {
      refetchCamera();
    } else if (activeTab === "languagePopUp") {
      refetchLanguage();
    } else if (activeTab === "purchaseLoginPopUp") {
      refetchPurchaseLogin();
    } else if (activeTab === "loginPopUp") {
      refetchLogin();
    } else if (activeTab === "logoutPopUp") {
      refetchLogout();
    } else if (activeTab === "VMSPopUp") {
      refetchVMS();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!query.get("activeTab")) {
      navigate(`/dedicated/magp/popup?activeTab=cameraAccess`, {
        replace: true,
      });
    }
  }, []);

  const cameraFormConfig: FormConfig = {
    initialValues: {
      name: drilldownDataById?.payload ? decodeHTML(drilldownDataById?.payload?.name) : decodeHTML(cameraData?.[0]?.name),
      field_access_permission_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_access_permission_ms)
        : decodeHTML(cameraData?.[0]?.field_access_permission_ms),
      field_access_permission_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_access_permission_zh)
        : decodeHTML(cameraData?.[0]?.field_access_permission_zh),
      field_allow_btn_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_allow_btn_en)
        : decodeHTML(cameraData?.[0]?.field_allow_btn_en),
      field_allow_btn_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_allow_btn_ms)
        : decodeHTML(cameraData?.[0]?.field_allow_btn_ms),
      field_allow_btn_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_allow_btn_zh)
        : decodeHTML(cameraData?.[0]?.field_allow_btn_zh),
      field_cancel_btn_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_cancel_btn_en)
        : decodeHTML(cameraData?.[0]?.field_cancel_btn_en),
      field_cancel_btn_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_cancel_btn_ms)
        : decodeHTML(cameraData?.[0]?.field_cancel_btn_ms),
      field_cancel_btn_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_cancel_btn_zh)
        : decodeHTML(cameraData?.[0]?.field_cancel_btn_zh),
      field_confirm_text_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_confirm_text_en)
        : decodeHTML(cameraData?.[0]?.field_confirm_text_en),
      field_confirm_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_confirm_text_ms)
        : decodeHTML(cameraData?.[0]?.field_confirm_text_ms),
      field_confirm_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload?.field_confirm_text_zh)
        : decodeHTML(cameraData?.[0]?.field_confirm_text_zh),
    },
    fields: fields.cameraAccess,
  };

  const langformConfig: FormConfig = {
    initialValues: {
      field_language_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_language_en)
        : decodeHTML(languageData?.[0]?.field_language_en),
      field_language_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_language_ms)
        : decodeHTML(languageData?.[0]?.field_language_ms),
      field_language_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_language_zh)
        : decodeHTML(languageData?.[0]?.field_language_zh),
      name: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.name)
        : decodeHTML(languageData?.[0]?.name),
      field_name_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_name_ms)
        : decodeHTML(languageData?.[0]?.field_name_ms),
      field_name_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_name_zh)
        : decodeHTML(languageData?.[0]?.field_name_zh),
      field_bhasha_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_bhasha_en)
        : decodeHTML(languageData?.[0]?.field_bhasha_en),
      field_bhasha_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_bhasha_ms)
        : decodeHTML(languageData?.[0]?.field_bhasha_ms),
      field_bhasha_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_language_zh)
        : decodeHTML(languageData?.[0]?.field_bhasha_zh),
      field_mandrain_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_mandrain_en)
        : decodeHTML(languageData?.[0]?.field_mandrain_en),
      field_mandrain_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_mandrain_ms)
        : decodeHTML(languageData?.[0]?.field_mandrain_ms),
      field_mandrain_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_mandrain_zh)
        : decodeHTML(languageData?.[0]?.field_mandrain_zh),
      field_continue_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_continue_en)
        : decodeHTML(languageData?.[0]?.field_continue_en),
      field_continue_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_continue_ms)
        : decodeHTML(languageData?.[0]?.field_continue_ms),
      field_continue_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_continue_zh)
        : decodeHTML(languageData?.[0]?.field_continue_zh),
    },
    fields: fields.languagePopUp,
  };

  const loginformConfig: FormConfig = {
    initialValues: {
      field_login_image: [
        {
          uid: "-1",
          name: "field_login.png",
          status: "done",
          url: drilldownDataById?.payload
            ? drilldownDataById?.payload.field_login_image
            : login?.[0]?.field_login_image,
        },
      ],
      field_login_text: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_text)
        : decodeHTML(login?.[0].field_login_text),
      field_login_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_text_ms)
        : decodeHTML(login?.[0].field_login_text_ms),
      field_login_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_text_zh)
        : decodeHTML(login?.[0].field_login_text_zh),
      field_login_email_text: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_email_text)
        : decodeHTML(login?.[0].field_login_email_text),
      field_login_email_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_email_text_ms)
        : decodeHTML(login?.[0].field_login_email_text_ms),
      field_login_email_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_email_text_zh)
        : decodeHTML(login?.[0].field_login_email_text_zh),
      field_login_password: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_password)
        : decodeHTML(login?.[0].field_login_password),
      field_login_password_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_password_ms)
        : decodeHTML(login?.[0].field_login_password_ms),
      field_login_password_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_password_zh)
        : decodeHTML(login?.[0].field_login_password_zh),
      field_forgot_password: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_forgot_password)
        : decodeHTML(login?.[0].field_forgot_password),
      field_forgot_password_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_forgot_password_text_ms)
        : decodeHTML(login?.[0].field_forgot_password_text_ms),
      field_forgot_password_text_zn: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_forgot_password_text_zn)
        : decodeHTML(login?.[0].field_forgot_password_text_zn),
      field_login_show_image: [
        {
          uid: "-1",
          name: "showImage.png",
          status: "done",
          url: drilldownDataById?.payload ? drilldownDataById?.payload.showImage : login?.[0]?.showImage,
        },
      ],
      field_login_button_text: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_button_text)
        : decodeHTML(login?.[0].field_login_button_text),
      field_login_button_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_button_text_ms)
        : decodeHTML(login?.[0].field_login_button_text_ms),
      field_login_button_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_button_text_zh)
        : decodeHTML(login?.[0].field_login_button_text_zh),
      field_login_or_text: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_or_text)
        : decodeHTML(login?.[0].field_login_or_text),
      field_login_or_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_or_text_ms)
        : decodeHTML(login?.[0].field_login_or_text_ms),
      field_login_or_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_login_or_text_zh)
        : decodeHTML(login?.[0].field_login_or_text_zh),
      field_facebook_text: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_facebook_text)
        : decodeHTML(login?.[0].field_facebook_text),
      field_facebook_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_facebook_text_ms)
        : decodeHTML(login?.[0].field_facebook_text_ms),
      field_facebook_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_facebook_text_zh)
        : decodeHTML(login?.[0].field_facebook_text_zh),
      field_facebook_image: [
        {
          uid: "-1",
          name: "fbImage.png",
          status: "done",
          url: drilldownDataById?.payload ? drilldownDataById?.payload.fbImage : login?.[0]?.fbImage,
        },
      ],
      field_google_image: [
        {
          uid: "-1",
          name: "googleImage.png",
          status: "done",
          url: drilldownDataById?.payload ? drilldownDataById?.payload.googleImage : login?.[0]?.googleImage,
        },
      ],
      field_google_text: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_google_text)
        : decodeHTML(login?.[0].field_google_text),
      field_google_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_google_text_ms)
        : decodeHTML(login?.[0].field_google_text_ms),
      field_google_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_google_text_zh)
        : decodeHTML(login?.[0].field_google_text_zh),
      field_register_now_text_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_register_now_text_en)
        : decodeHTML(login?.[0].field_register_now_text_en),
      field_register_now_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_register_now_text_ms)
        : decodeHTML(login?.[0].field_register_now_text_ms),
      field_register_now_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_register_now_text_zh)
        : decodeHTML(login?.[0].field_register_now_text_zh),
      field_register_now_button: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_register_now_button)
        : decodeHTML(login?.[0].field_register_now_button),
      field_register_now_button_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_register_now_button_ms)
        : decodeHTML(login?.[0].field_register_now_button_ms),
      field_register_now_button_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_register_now_button_zh)
        : decodeHTML(login?.[0].field_register_now_button_zh),
      field_login_filled_image: [
        {
          uid: "-1",
          name: "filledImage.png",
          status: "done",
          url: drilldownDataById?.payload ? drilldownDataById?.payload.filledImage : login?.[0]?.filledImage,
        },
      ],
      field_web_login_icon: [
        {
          uid: "-1",
          name: "webLoginIcon.png",
          status: "done",
          url: drilldownDataById?.payload ? drilldownDataById?.payload.webLoginIcon : login?.[0]?.webLoginIcon,
        },
      ],
      field_web_login_image: [
        {
          uid: "-1",
          name: "webLoginImage.png",
          status: "done",
          url: drilldownDataById?.payload ? drilldownDataById?.payload.webLoginImage : login?.[0]?.webLoginImage,
        },
      ],
      field_apple_text_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_apple_text_en)
        : decodeHTML(login?.[0].field_apple_text_en),
      field_apple_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_apple_text_ms)
        : decodeHTML(login?.[0].field_apple_text_ms),
      field_apple_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_apple_text_zh)
        : decodeHTML(login?.[0].field_apple_text_zh),
      field_apple_image: [
        {
          uid: "-1",
          name: "appleImage.png",
          status: "done",
          url: drilldownDataById?.payload ? drilldownDataById?.payload.appleImage : login?.[0]?.appleImage,
        },
      ],
      published: drilldownDataById?.payload ? drilldownDataById?.payload.published : login?.[0]?.published,
    },
    fields: fields.loginPopUp,
  };

  const purchaseLoginformConfig: FormConfig = {
    initialValues: {
      field_purchase_login_pop_image: [
        {
          uid: "-1",
          name: "purchase_login.png",
          status: "done",
          url: drilldownDataById?.payload
            ? drilldownDataById?.payload.field_purchase_login_pop_image
            : purchaseLoginData?.[0]?.field_purchase_login_pop_image,
        },
      ],
      name: drilldownDataById?.payload ? drilldownDataById?.payload.name : purchaseLoginData?.[0]?.name,
      field_purchase_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_purchase_text_ms)
        : decodeHTML(purchaseLoginData?.[0]?.field_purchase_text_ms),
      field_purchase_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_purchase_zh)
        : decodeHTML(purchaseLoginData?.[0].field_purchase_text_zh),
      field_purchase_login_pop_button: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_purchase_login_pop_button)
        : decodeHTML(purchaseLoginData?.[0].field_purchase_login_pop_button),
      field_purchase_login_button_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_purchase_login_button_ms)
        : decodeHTML(purchaseLoginData?.[0].field_purchase_login_button_ms),
      field_purchase_login_button_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_purchase_login_button_zh)
        : decodeHTML(purchaseLoginData?.[0].field_purchase_login_button_zh),
    },
    fields: fields.purchaseLoginPopUp,
  };

  const logoutformConfig: FormConfig = {
    initialValues: {
      field_logout_image: [
        {
          uid: "-1",
          name: "logout.png",
          status: "done",
          url: drilldownDataById?.payload
            ? drilldownDataById?.payload.field_logout_image
            : logoutData?.[0]?.field_logout_image,
        },
      ],
      name: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.name)
        : decodeHTML(logoutData?.[0]?.name),
      field_logout_txt_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_logout_txt_ms)
        : decodeHTML(logoutData?.[0]?.field_logout_txt_ms),
      field_logout_txt_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_logout_txt_zh)
        : decodeHTML(logoutData?.[0]?.field_logout_txt_zh),
      field_no_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_no_text_ms)
        : decodeHTML(logoutData?.[0]?.field_no_text_ms),
      field_no_text_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_no_text_en)
        : decodeHTML(logoutData?.[0]?.field_no_text_en),
      field_no_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_no_text_zh)
        : decodeHTML(logoutData?.[0]?.field_no_text_zh),
      field_yes_confirm_text_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_yes_confirm_text_en)
        : decodeHTML(logoutData?.[0]?.field_yes_confirm_text_en),
      field_yes_confirm_txt_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_yes_confirm_txt_ms)
        : decodeHTML(logoutData?.[0]?.field_yes_confirm_txt_ms),
      field_yes_confirm_txt_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_yes_confirm_txt_zh)
        : decodeHTML(logoutData?.[0]?.field_yes_confirm_txt_zh),
    },
    fields: fields.logoutPopUp,
  };

  const VMSFormConfig: FormConfig = {
    initialValues: {
      name: drilldownDataById?.payload ? drilldownDataById?.payload.name : VMSData?.[0]?.name,
      field_continue_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_continue_text_ms)
        : decodeHTML(VMSData?.[0]?.field_continue_text_ms),
      field_continue_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_continue_text_zh)
        : decodeHTML(VMSData?.[0]?.field_continue_text_zh),
      field_vms_text: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_vms_text)
        : decodeHTML(VMSData?.[0]?.field_vms_text),
      field_vms_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_vms_text_ms)
        : decodeHTML(VMSData?.[0]?.field_vms_text_ms),
      field_vms_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_vms_text_ms)
        : decodeHTML(VMSData?.[0]?.field_vms_text_zh),
      field_vms_button: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_vms_button)
        : decodeHTML(VMSData?.[0]?.field_vms_button),
      field_vms_button_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_vms_button_ms)
        : decodeHTML(VMSData?.[0]?.field_vms_button_ms),
      field_vms_button_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_vms_button_ms)
        : decodeHTML(VMSData?.[0]?.field_vms_button_zh),
    },
    fields: fields.VMSPopUp,
  };

  const welcomeBannerformConfig: FormConfig = {
    initialValues: {
      field_banner_popup_image: [
        {
          uid: "-1",
          name: "field_banner_popup_image.png",
          status: "done",
          url: drilldownDataById?.payload
            ? decodeHTML(drilldownDataById?.payload.field_banner_popup_image)
            : decodeHTML(welcomeBannerData?.[0]?.field_banner_popup_image),
        },
      ],
      field_web_popup: [
        {
          uid: "-1",
          name: "field_web_popup.png",
          status: "done",
          url: drilldownDataById?.payload
            ? decodeHTML(drilldownDataById?.payload.field_web_popup)
            : decodeHTML(welcomeBannerData?.[0]?.field_web_popup),
        },
      ],
      field_redirect_url: drilldownDataById?.payload ? decodeHTML(drilldownDataById?.payload.field_redirect_url) : decodeHTML(welcomeBannerData?.[0]?.field_redirect_url),
      field_image_text_en: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_image_text_en)
        : decodeHTML(welcomeBannerData?.[0]?.field_image_text_en),
      field_image_text_ms: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_image_text_ms)
        : decodeHTML(welcomeBannerData?.[0]?.field_image_text_ms),
      field_image_text_zh: drilldownDataById?.payload
        ? decodeHTML(drilldownDataById?.payload.field_image_text_zh)
        : decodeHTML(welcomeBannerData?.[0]?.field_image_text_zh),
      field_banner_popup_published: drilldownDataById?.payload ? decodeHTML(drilldownDataById?.payload?.field_banner_popup_published) : decodeHTML(welcomeBannerData?.[0]?.field_banner_popup_published),
      field_banner_description_en: drilldownDataById
        ? decodeHTML(drilldownDataById?.payload?.field_banner_description_en?.value)
        : welcomeBannerData
          ? decodeHTML(welcomeBannerData?.[0]?.field_banner_description_en)
          : "",
      field_banner_description_mal: drilldownDataById ? decodeHTML(drilldownDataById?.payload?.field_banner_description_mal?.value) : welcomeBannerData ? decodeHTML(welcomeBannerData?.[0]?.field_banner_description_mal) : "",
      field_banner_description_man: drilldownDataById
        ? decodeHTML(drilldownDataById?.payload?.field_banner_description_man?.value)
        : welcomeBannerData
          ? decodeHTML(welcomeBannerData?.[0]?.field_banner_description_man)
          : "",
      field_title_color: drilldownDataById?.payload
          ? decodeHTML(drilldownDataById?.payload?.field_title_color)
          : decodeHTML(welcomeBannerData?.[0]?.field_title_color),
      field_description_color: drilldownDataById?.payload
          ? decodeHTML(drilldownDataById?.payload?.field_description_color)
          : decodeHTML(welcomeBannerData?.[0]?.field_description_color),
    },
    fields: fields.welcomeBanner,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    // for (let key in data) {
    //   if (typeof data[key] === "string" && !excludedKeys.includes(key)) {
    //     data[key] = encodeHTML(data[key]);
    //   }
    // }
    let imageKeys: any = [
      "field_apple_image",
      "fbImage",
      "field_facebook_image",
      "field_google_image",
      "field_login_image",
      "filledImage",
      "googleImage",
      "showImage",
      "field_logout_image",
      "appleImage",
      "webLoginImage",
      "webLoginIcon",
      "field_web_login_icon",
      "field_web_login_image",
      "field_login_show_image",
      "field_login_filled_image",
      "field_purchase_login_pop_image",
      "field_banner_popup_image",
      "field_web_popup"
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];

        if (field) {
          if (field[0] && field[0].originFileObj) {
            const formData = new FormData();
            formData.append("upload-file", field[0].originFileObj);
            formData.append("application", "MAGP");
            const res: any = await post("management/upload-file", formData);

            if (res) {
              data[key] = res.response.data;
            }
          } else {
            data[key] = data[key][0] && data[key][0].url;
          }
        }
      } catch (error) {}
    }

    let descKeys: any = [
      "field_banner_description_mal",
      "field_banner_description_man",
      "field_banner_description_en",
    ];
    for (const key of descKeys) {
      try {
        data[key] = {
          value: data[key] || "",
          format: "full_html",
        };
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "POPUP",
      subModule: query?.get("activeTab"),
      application: "MAGP",
      type: "DEDICATED",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload : {
        ...data,
        ...(activeTab !== "loginPopUp" && { published: true })
      },
      status: status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response:any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response:any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
    if (status == "PENDING") {
      navigate("/dedicated/status-information?activeTab=pending");
    } else{
      navigate("/dedicated/status-information?activeTab=draft");
    }
  };

  const items = [
    {
      label: "Camera Access",
      key: "cameraAccess",
      children: (
        <DynamicForm
          isLoading={isLoading || isLoadingDoc}
          config={cameraFormConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/camera_access_popup/${cameraData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
    {
      label: "Language",
      key: "languagePopUp",
      children: (
        <DynamicForm
          config={langformConfig}
          isLoading={languageLoading || isLoadingDoc}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/language-popup/${languageData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },

    {
      label: "Login",
      key: "loginPopUp",
      children: (
        <DynamicForm
          config={loginformConfig}
          isLoading={loginLoading || isLoadingDoc}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/login-screen-popup/3`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },

    {
      label: "Logout",
      key: "logoutPopUp",
      children: (
        <DynamicForm
          isLoading={logoutLoading || isLoadingDoc}
          config={logoutformConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/logout-popup/${logoutData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },

    {
      label: "VMS",
      key: "VMSPopUp",
      children: (
        <DynamicForm
          isLoading={VMSLoading || isLoadingDoc}
          config={VMSFormConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/vms-popup/${VMSData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
    {
      label: "Purchase Login",
      key: "purchaseLoginPopUp",
      children: (
        <DynamicForm
          isLoading={purchaseLoginLoading || isLoadingDoc}
          config={purchaseLoginformConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/purchase-login-popup/${purchaseLoginData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
    {
      label: "Welcome Banner",
      key: "welcomeBanner",
      children: (
        <DynamicForm
          isLoading={welcomeBannerLoading || isLoadingDoc}
          config={welcomeBannerformConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/welcome-banner-popup/${welcomeBannerData?.[0]?.id || drilldownDataById?.payload?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
  ];

  return (
    <ContentWrapper
      title={"Popup"}
      // loading={
      //   isLoading ||
      //   purchaseLoginLoading ||
      //   logoutLoading ||
      //   languageLoading ||
      //   loginLoading ||
      //   VMSLoading ||
      //   isLoadingDoc
      // }
    >
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`);
        }}
      ></Tabs>
    </ContentWrapper>
  );
};
